import { Button, Card, CardActions, CardContent, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAllRobots from 'hooks/useAllRobots'
import { IDiagnosticNotification } from 'utils/interfaces'

interface Props {
  notification: IDiagnosticNotification
  onAction: (buttonId: string) => void
  /** Ид нажатой кнопки, пока идёт обработка. */
  loadingButtonId?: string
}

const DiagnosticNotification = ({ notification, onAction, loadingButtonId }: Props) => {
  const { t } = useTranslation()
  const { data: robots = [] } = useAllRobots()
  const [timeLeft, setTimeLeft] = useState(notification.timeout)
  const initialTimeout = notification.timeout

  useEffect(() => {
    if (timeLeft <= 0) return

    const timer = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 100))
    }, 100)

    return () => clearInterval(timer)
  }, [timeLeft])

  const getSeverityColor = (code: number) => {
    if (code >= 1000 && code < 2000) return '#f44336' // Error
    if (code >= 2000 && code < 3000) return '#ff9800' // Warning
    return '#2196f3' // Info
  }

  const color = getSeverityColor(notification.code)
  const robotName = robots.find(r => r.id === notification.robot_id)?.name
  const isLoading = loadingButtonId !== undefined

  return (
    <Card
      sx={{
        minWidth: 300,
        maxWidth: 400,
        borderLeft: `6px solid ${color}`,
        mb: 1,
        boxShadow: 3,
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          {t('diagnostics.robot', { robot: robotName || `#${notification.robot_id}` })}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {notification.message}
        </Typography>
        {initialTimeout > 0 && (
          <LinearProgress
            variant="determinate"
            value={(timeLeft / initialTimeout) * 100}
            sx={{
              mt: 0.5,
              height: 3,
              borderRadius: 2,
              backgroundColor: '#eee',
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
              },
            }}
          />
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 1, pb: 1 }}>
        <Stack direction="row" spacing={1}>
          {notification.buttons.map(btn => (
            <Button
              key={btn.id}
              size="small"
              variant="outlined"
              disabled={isLoading}
              startIcon={loadingButtonId === btn.id ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() => onAction(btn.id)}
              sx={{
                color: color,
                borderColor: color,
                '&:hover': {
                  borderColor: color,
                  backgroundColor: `${color}11`,
                },
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Stack>
      </CardActions>
    </Card>
  )
}

export default DiagnosticNotification
